<template>
  <Dialog
    v-model:visible="visible"
    modal
    :style="{ width: '45rem' }"
    class="rounded-sm user-details-modal"
    :closable="true"
    :dismissableMask="false"
  >
    <template #header>
      <h2 class="text-xl font-light p-1">User details</h2>
    </template>

    <div v-if="loading" class="flex justify-center items-center py-8">
      <ProgressSpinner style="width: 50px; height: 50px" />
    </div>

    <div v-else-if="error" class="text-center py-8 text-red-500">
      <p>{{ error }}</p>
    </div>

    <div v-else-if="user" class="profile-content flex mb-5">
      <!-- Avatar section -->
      <div class="avatar w-1/5 mt-5 ms-3">
        <div v-if="user.avatar && user.avatar.url" class="flex items-center justify-center">
          <img :src="user.avatar.url" :alt="user.last_name" class="rounded-full w-20 h-20 object-cover" />
        </div>
        <div v-else class="flex items-center justify-center">
          <div class="rounded-full w-20 h-20 bg-blue-500 text-white flex items-center justify-center text-2xl font-bold">
            {{ getInitials(user.first_name, user.last_name) }}
          </div>
        </div>
      </div>

      <!-- User details section -->
      <div class="flex-col sm:w-2/3" v-if="user.first_name && user.last_name">
        <div class="sm:order-none order-3 items-end self-end flex flex-center">
          <div class="border-1 w-20 border-top-color my-3"></div>
          <div class="w-full">
            <!-- Name and Role -->
            <div class="flex items-center my-1">
              <h2 class="text-lg font-bold text-gray-800 me-2">
                {{ user.first_name }} {{ user.last_name }}
              </h2>
              <Tag
                v-if="user.role && user.role.name"
                :value="user.role.name"
                severity="info"
                class="ml-2"
              />
            </div>

            <!-- Email -->
            <div class="flex items-center my-1">
              <i class="pi pi-at w-6 text-gray-400"></i>
              <div class="ml-2 truncate text-blue-600">
                {{ user.email }}
              </div>
            </div>

            <!-- Phone Number -->
            <div v-if="user.phone_number" class="flex items-center my-1">
              <i class="pi pi-phone w-6 text-gray-400"></i>
              <div class="ml-2">{{ user.phone_number }}</div>
            </div>

            <!-- System ID -->
            <div class="flex items-center my-1">
              <i class="pi pi-id-card w-6 text-gray-400"></i>
              <div class="text-gray-600">
                <span class="text-gray-800 font-semibold">System ID: </span>
                {{ user.id }}
              </div>
            </div>

            <!-- Joined Date -->
            <div v-if="user.inserted_at" class="flex items-center my-1">
              <i class="pi pi-calendar w-6 text-gray-400"></i>
              <div class="text-gray-600">
                <span class="text-gray-800 font-semibold">Joined: </span>
                {{ formatTimeAgo(user.inserted_at) }}
              </div>
            </div>

            <!-- Last Seen -->
            <div v-if="user.last_seen_at" class="flex items-center my-1">
              <i class="pi pi-eye w-6 text-gray-400"></i>
              <div class="text-gray-600">
                <span class="text-gray-800 font-semibold">Last seen at: </span>
                {{ formatTimeAgo(user.last_seen_at) }}
              </div>
            </div>

            <!-- Last Sign In IP -->
            <div v-if="user.last_sign_in_ip" class="flex items-center my-1">
              <i class="pi pi-globe w-6 text-gray-400"></i>
              <div class="text-gray-600">
                <span class="text-gray-800 font-semibold">Last sign in IP: </span>
                {{ user.last_sign_in_ip }}
              </div>
            </div>

            <!-- Sign In Count -->
            <div v-if="user.sign_in_count !== undefined" class="flex items-center my-1">
              <i class="pi pi-sort-numeric-up w-6 text-gray-400"></i>
              <div class="text-gray-600">
                <span class="text-gray-800 font-semibold">Sign in count: </span>
                {{ user.sign_in_count }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>

<script setup>
import { ref, watch } from 'vue'
import Dialog from 'primevue/dialog'
import Tag from 'primevue/tag'
import ProgressSpinner from 'primevue/progressspinner'
import axios from 'axios'
import { formatDistanceToNow } from 'date-fns'

const props = defineProps({
  userId: {
    type: [String, Number],
    default: null
  },
  tenant: {
    type: String,
    required: true
  },
  visible: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:visible'])

const visible = ref(false)
const user = ref(null)
const loading = ref(false)
const error = ref(null)

// Watch for visibility changes
watch(() => props.visible, (newVal) => {
  visible.value = newVal
  if (newVal && props.userId) {
    loadUser(props.userId)
  }
})

watch(() => visible.value, (newVal) => {
  emit('update:visible', newVal)
  if (!newVal) {
    // Reset state when dialog closes
    user.value = null
    error.value = null
  }
})

// Load user data from API
const loadUser = async (userId) => {
  if (!userId || !props.tenant) {
    error.value = 'User ID and tenant are required'
    return
  }

  loading.value = true
  error.value = null

  try {
    const response = await axios.get(`/api/v1/users_proxy/${userId}`, {
      params: { tenant: props.tenant }
    })

    if (response.data) {
      user.value = response.data
    } else {
      error.value = 'User data not found'
    }
  } catch (err) {
    console.error('Error loading user:', err)
    if (err.response && err.response.data && err.response.data.error) {
      error.value = err.response.data.error
    } else {
      error.value = 'Failed to load user details'
    }
  } finally {
    loading.value = false
  }
}

// Get user initials for avatar
const getInitials = (firstName, lastName) => {
  if (!firstName && !lastName) return '?'
  const first = firstName ? firstName.charAt(0).toUpperCase() : ''
  const last = lastName ? lastName.charAt(0).toUpperCase() : ''
  return first + last
}

// Format time ago
const formatTimeAgo = (dateString) => {
  if (!dateString) return 'N/A'
  try {
    const date = new Date(dateString)
    return formatDistanceToNow(date, { addSuffix: true })
  } catch (e) {
    return dateString
  }
}

// Expose methods for parent component
defineExpose({
  setVisible: (val, userId) => {
    visible.value = val
    if (val && userId) {
      loadUser(userId)
    }
  }
})
</script>

<style scoped>
.user-details-modal :deep(.p-dialog-header) {
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.user-details-modal :deep(.p-dialog-content) {
  padding: 1.5rem;
}

.border-top-color {
  border-top: 2px solid #3b82f6;
}

.flex-center {
  display: flex;
  flex-direction: column;
}

/* Icon styling */
.pi {
  font-size: 1rem;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .profile-content {
    flex-direction: column;
  }

  .avatar {
    width: 100%;
    display: flex;
    justify-content: center;
    margin-bottom: 1rem;
  }

  .flex-col {
    width: 100% !important;
  }
}
</style>
